/**
 * Human-readable outcome text: the ONE place that turns outcomeMeta names/descriptions into titles, side labels,
 * categories, resolution times and detail rows. Pure (no React); hooks live in useDescribe.ts.
 *
 * Order of precedence for an outcome:
 *  1. a renderer for its template family (binaryPrice*, priceTouch*, sportsContestWinner*, …; see FAMILY_TITLES);
 *  2. the template's own title format from outcomeTemplates (live list merged over the bundled snapshot);
 *  3. the raw name + description (never crash, never hide data).
 * An outcome that belongs to a question (categorical market) is titled "<question title>: <its own label>".
 */
import { sideName } from '../hl/outcomes';
import type { OutcomeSpec, QuestionSpec } from '../hl/types';
import {
  cleanDecimal,
  formatHlTime,
  formatNumber,
  formatUtc,
  formatWithUnit,
  humanizeKey,
  parseHlTime,
  priceUnit,
  roundDecimal,
  stripDex,
  type TimeFormatOptions,
  type Unit,
} from './format';
import { isKeyValueDescription, parseDescription } from './parse';
import { familyOf, snapshotRegistry, templateId, type TemplateInfo, type TemplateRegistry } from './templates';
import { TOUCH_FAMILY, touchInfo, type TouchInfo } from './touch';
import { getLocale, t, type PlainKey } from '../i18n/i18n';

export type Category = 'crypto price' | 'sports' | 'event' | 'other';

const CATEGORY_KEY: Record<Category, PlainKey> = {
  'crypto price': 'category.price',
  sports: 'describe.category.sports',
  event: 'describe.category.event',
  other: 'category.other',
};

/** Chip text per category, in the active language (getters, #799). */
export const CATEGORY_LABEL: Readonly<Record<Category, string>> = Object.defineProperties(
  {} as Record<Category, string>,
  Object.fromEntries(
    (Object.keys(CATEGORY_KEY) as Category[]).map((c) => [c, { enumerable: true, get: () => t(CATEGORY_KEY[c]) }]),
  ),
);

export interface DetailRow {
  label: string;
  /** Unknown template field: `label` is the raw field name made readable, so it is data, not a catalog string. */
  labelIsData?: true;
  value: string;
  /** Hover text, e.g. the UTC time next to a local time. */
  hint?: string;
}

export interface OutcomeDescription {
  outcome: number;
  /** "BTC above $81,300 at Oct 17, 01:45 UTC?", "Baltimore Ravens vs Cleveland Browns", "2026 Ballon d'Or winner: Harry Kane". */
  title: string;
  subtitle?: string;
  /** Own label inside a question ("Harry Kane", "Draw", "Other"); undefined for standalone outcomes. */
  label?: string;
  /** [side 0, side 1] from sideSpecs ("Yes"/"No", "Ravens"/"Browns", "Over"/"Under", "Long"/"Short"). */
  sideLabels: [string, string];
  resolvesAt?: Date;
  /** What resolvesAt is: "Resolves", "Starts", "Decision" or "Deadline". */
  resolvesLabel?: string;
  category: Category;
  details: DetailRow[];
  /** Template id ("binaryPrice6"), if any. */
  template?: string;
  /** Price touch market (#786, priceTouch* family): perp, target, deadline, TWAP rule. resolvesLabel is "Deadline". */
  touch?: TouchInfo;
  question?: number;
  questionTitle?: string;
  /** True when no renderer understood the outcome and title/subtitle are the raw name/description. */
  raw: boolean;
}

export interface QuestionDescription {
  question: number;
  title: string;
  subtitle?: string;
  category: Category;
  resolvesAt?: Date;
  resolvesLabel?: string;
  details: DetailRow[];
  template?: string;
  raw: boolean;
}

export interface DescribeOptions extends TimeFormatOptions {
  /** Template formats for the current env (default: the bundled mainnet snapshot). */
  templates?: TemplateRegistry;
}

/** Anything with the outcomeMeta `questions` list (the full response is fine). */
/** Loose outcome shape (outcomeMeta element; partial objects from older call sites are fine). */
export interface OutcomeLike {
  outcome: number;
  name?: string;
  description?: string;
  sideSpecs?: { name: string }[];
}

export interface MetaLike {
  outcomes?: readonly OutcomeSpec[];
  questions?: readonly QuestionSpec[];
}

type Desc = Record<string, string>;

// ---------------------------------------------------------------------------------------------
// Value formatting
// ---------------------------------------------------------------------------------------------

const PRICE_FAMILIES = new Set(['binaryPrice', 'binaryPriceExternal', 'priceTouch', 'scalarPrice', 'scalePrice']);
const PRICE_KEYS = new Set(['threshold', 'target', 'low', 'high', 'targetPrice']);
const TIME_KEYS = new Set([
  'time',
  'dateTime',
  'expiry',
  'scheduledStart',
  'scheduledDecision',
  'resolutionDeadline',
  'decisionDeadline',
  'listingDeadline',
]);

function unitFor(key: string, family: string, d: Desc): Unit {
  if (PRICE_KEYS.has(key) && (PRICE_FAMILIES.has(family) || family === 'Recurring'))
    return priceUnit(d.perp ?? d.underlying ?? d.shortName, [d.priceDescription, d.instrument].filter(Boolean).join(' '));
  if (key === 'lowerBps' || key === 'upperBps') return 'bps';
  return 'none';
}

interface Ctx {
  family: string;
  d: Desc;
  opts: DescribeOptions;
}

/** Value as it appears inside a title. */
function v(ctx: Ctx, key: string): string {
  const raw = ctx.d[key];
  if (raw === undefined || raw === '') return '';
  if (TIME_KEYS.has(key)) return formatHlTime(raw, ctx.opts);
  if (key === 'perp') return stripDex(raw);
  const unit = unitFor(key, ctx.family, ctx.d);
  if (unit !== 'none') return formatWithUnit(raw, unit);
  if (key === 'line' || key === 'marketCapThresholdB' || key === 'seconds') return formatNumber(cleanDecimal(raw));
  return raw;
}

/** Fill a "{key} …" format; null when a placeholder has no value. */
function fill(format: string, ctx: Ctx): string | null {
  let missing = false;
  const out = format.replace(/\{(\w+)\}/g, (_, k: string) => {
    const val = v(ctx, k);
    if (!val) missing = true;
    return val;
  });
  return missing ? null : out.replace(/\s+/g, ' ').trim();
}

const join = (...parts: (string | undefined | null | false)[]) => parts.filter(Boolean).join(' ');
const dot = (...parts: (string | undefined | null | false)[]) => parts.filter(Boolean).join(' · ');

function periodLabel(p: string | undefined): string | undefined {
  if (!p) return undefined;
  const known: Record<string, string> = { '1d': 'daily', '1h': 'hourly', '15m': 'every 15 minutes', '1w': 'weekly' };
  return known[p] ?? `every ${p}`;
}

/** "the Hyperliquid BTC perp mark price", 30 → "Hyperliquid BTC perp mark price · 30s TWAP". */
function priceSource(d: Desc): string | undefined {
  const src = d.priceDescription?.replace(/^the\s+/i, '');
  const twap = d.seconds ? `${formatNumber(d.seconds)}s TWAP` : undefined;
  return dot(src, twap) || undefined;
}

// ---------------------------------------------------------------------------------------------
// Details
// ---------------------------------------------------------------------------------------------

/**
 * Detail-row labels (catalog keys). Keep every keyword of templates.snapshot.ts here (describe.test.ts checks it). A
 * template added later falls back to humanizeKey, shown as data (`labelIsData`, translate="no") until it gets a key.
 */
const LABELS: Record<string, PlainKey> = {
  perp: 'touch.perp',
  underlying: 'describe.label.underlying',
  threshold: 'describe.label.threshold',
  target: 'touch.target',
  targetPrice: 'touch.target_price',
  low: 'describe.label.low',
  high: 'describe.label.high',
  time: 'describe.label.settles_at',
  expiry: 'describe.label.settles_at',
  dateTime: 'describe.label.date',
  seconds: 'describe.label.twap_window',
  priceDescription: 'describe.label.price_source',
  feed: 'describe.label.price_feed',
  instrument: 'describe.label.instrument',
  shortName: 'describe.label.short_name',
  period: 'describe.label.period',
  class: 'describe.label.series',
  priceThresholds: 'describe.label.bucket_thresholds',
  participantA: 'describe.label.side_a',
  participantB: 'describe.label.side_b',
  shortNameA: 'describe.label.short_name_a',
  shortNameB: 'describe.label.short_name_b',
  scheduledStart: 'describe.label.scheduled_start',
  scheduledDecision: 'describe.label.scheduled_decision',
  resolutionDeadline: 'describe.label.resolution_deadline',
  decisionDeadline: 'describe.label.decision_deadline',
  listingDeadline: 'describe.label.listing_deadline',
  officialSource: 'describe.label.official_source',
  countedPlay: 'describe.label.counted_play',
  contestType: 'describe.label.contest_type',
  marketCapThresholdB: 'describe.label.market_cap_threshold',
  lowerBps: 'describe.label.lower_bound',
  upperBps: 'describe.label.upper_bound',
  competition: 'describe.label.competition',
  stage: 'describe.label.stage',
  season: 'describe.label.season',
  sport: 'describe.label.sport',
  participant: 'describe.label.participant',
  candidate: 'describe.label.candidate',
  spread: 'describe.label.spread',
  line: 'describe.label.line',
  measure: 'describe.label.measure',
  company: 'describe.label.company',
  award: 'describe.label.award',
  awardingBody: 'describe.label.awarding_body',
  field: 'describe.label.field',
  subject: 'describe.label.subject',
  entry: 'describe.label.entry',
  displayName: 'describe.label.display_name',
  institution: 'describe.label.institution',
  decisionLabel: 'describe.label.decision_label',
  policyMeasure: 'describe.label.policy_measure',
  bucketLabel: 'describe.label.bucket_label',
  event: 'describe.label.event',
  modelA: 'describe.label.model_a',
  modelB: 'describe.label.model_b',
  metric: 'describe.label.metric',
  comparisonDirection: 'describe.label.comparison_direction',
  leaderboard: 'describe.label.leaderboard',
  leaderboardUrl: 'describe.label.leaderboard_url',
  benchmarkSettings: 'describe.label.benchmark_settings',
  tiebreakers: 'describe.label.tiebreakers',
  index: 'describe.label.index',
};

const ORDER = [
  'underlying',
  'perp',
  'shortName',
  'instrument',
  'threshold',
  'target',
  'targetPrice',
  'low',
  'high',
  'priceThresholds',
  'time',
  'expiry',
  'dateTime',
  'period',
  'priceDescription',
  'feed',
  'seconds',
  'competition',
  'stage',
  'season',
  'sport',
  'participantA',
  'participantB',
  'participant',
  'candidate',
  'spread',
  'line',
  'measure',
  'scheduledStart',
  'countedPlay',
];

function detailValue(ctx: Ctx, key: string, raw: string): DetailRow {
  // #786: a touch market's {time} is a touch-by deadline, not a settlement time.
  const lk = Object.prototype.hasOwnProperty.call(LABELS, key) ? LABELS[key] : undefined;
  const head: Pick<DetailRow, 'label' | 'labelIsData'> =
    key === 'time' && ctx.family === TOUCH_FAMILY ? { label: t('describe.when.deadline') } : lk ? { label: t(lk) } : { label: humanizeKey(key), labelIsData: true };
  const at = TIME_KEYS.has(key) ? parseHlTime(raw) : undefined;
  if (at) return { ...head, value: formatHlTime(raw, ctx.opts), hint: formatUtc(at) };
  if (key === 'seconds') {
    const n = Number(raw);
    return { ...head, value: Number.isInteger(n) ? t('describe.detail.seconds', { n }) : formatNumber(raw) };
  }
  if (key === 'marketCapThresholdB') return { ...head, value: `$${formatNumber(cleanDecimal(raw))}B` };
  if (key === 'priceThresholds')
    return { ...head, value: raw.split(',').map((x) => formatWithUnit(x, priceUnit(ctx.d.underlying))).join(', ') };
  const unit = unitFor(key, ctx.family, ctx.d);
  if (unit === 'none') return { ...head, value: raw };
  const value = formatWithUnit(raw, unit);
  // Display precision may round (81457.954999 → $81,457.95); the exact on-chain value stays in the hover text.
  return cleanDecimal(raw, unit) !== roundDecimal(raw, 64) ? { ...head, value, hint: t('describe.detail.exact', { value: formatNumber(raw) }) } : { ...head, value };
}

function detailsOf(ctx: Ctx): DetailRow[] {
  const keys = Object.keys(ctx.d).filter((k) => ctx.d[k] !== '');
  const rank = (k: string) => {
    const i = ORDER.indexOf(k);
    return i < 0 ? ORDER.length : i;
  };
  return keys
    .map((k, i) => ({ k, i }))
    .sort((a, b) => rank(a.k) - rank(b.k) || a.i - b.i)
    .map(({ k }) => detailValue(ctx, k, ctx.d[k] ?? ''));
}

// ---------------------------------------------------------------------------------------------
// Categories and resolution times
// ---------------------------------------------------------------------------------------------

/** Free text may end with " metadata=category:sports|subCategory:…" (same convention as outcomeTemplates). */
function splitMetadata(text: string): { text: string; category?: string } {
  const i = text.indexOf('metadata=');
  if (i < 0) return { text };
  const cat = /metadata=category:([^|]+)/.exec(text.slice(i));
  return { text: text.slice(0, i).trim(), category: cat?.[1]?.trim() };
}

function categoryOf(family: string, info: TemplateInfo | undefined, reg: TemplateRegistry, explicit?: string): Category {
  let cat = explicit ?? info?.category;
  if (!cat && info?.parent) cat = reg.get(info.parent)?.category;
  switch (cat) {
    case 'price':
      return 'crypto price';
    case 'sports':
      return 'sports';
    case undefined:
      break;
    default:
      return 'event';
  }
  if (PRICE_FAMILIES.has(family) || family === 'Recurring') return 'crypto price';
  if (family.startsWith('sports')) return 'sports';
  if (/^(award|policy|company|aiModel)/.test(family)) return 'event';
  return 'other';
}

const RESOLVE_KEYS: [string, string][] = [
  ['time', 'Resolves'],
  ['expiry', 'Resolves'],
  ['scheduledStart', 'Starts'],
  ['scheduledDecision', 'Decision'],
  ['dateTime', 'Resolves'],
  ['listingDeadline', 'Deadline'],
  ['resolutionDeadline', 'Deadline'],
  ['decisionDeadline', 'Deadline'],
];

function resolution(d: Desc, family = ''): { resolvesAt?: Date; resolvesLabel?: string } {
  for (const [k, label] of RESOLVE_KEYS) {
    const t = parseHlTime(d[k]);
    // #786: touch markets end as soon as the target is touched; {time} is only the deadline.
    if (t) return { resolvesAt: t, resolvesLabel: family === TOUCH_FAMILY && k === 'time' ? 'Deadline' : label };
  }
  return {};
}

// ---------------------------------------------------------------------------------------------
// Family renderers
// ---------------------------------------------------------------------------------------------

interface Rendered {
  title: string;
  subtitle?: string;
}

type Renderer = (ctx: Ctx) => Rendered | null;

const vs = (ctx: Ctx) => (ctx.d.participantA && ctx.d.participantB ? `${ctx.d.participantA} vs ${ctx.d.participantB}` : null);
const contest = (ctx: Ctx) => join(ctx.d.competition, ctx.d.stage) || undefined;

/** Standalone outcome families. */
const OUTCOME_RENDERERS: Record<string, Renderer> = {
  binaryPrice: (c) => {
    const t = fill('{perp} above {threshold} at {time}?', c);
    return t ? { title: t, subtitle: priceSource(c.d) } : null;
  },
  binaryPriceExternal: (c) => {
    const t = fill('{shortName} above {threshold} on {time}?', c);
    return t ? { title: t, subtitle: dot(c.d.instrument, c.d.seconds && `${formatNumber(c.d.seconds)}s TWAP`) || undefined } : null;
  },
  priceTouch: (c) => {
    const t = fill('Will {perp} touch {target} by {time}?', c);
    return t ? { title: t, subtitle: priceSource(c.d) } : null;
  },
  scalarPrice: (c) => {
    const t = fill('{perp} from {low} to {high} at {time}', c);
    return t ? { title: t, subtitle: dot('Scalar payout', priceSource(c.d)) } : null;
  },
  sportsContestWinner: (c) => {
    const t = vs(c);
    return t ? { title: t, subtitle: contest(c) } : null;
  },
  sportsSpread: (c) => {
    const t = vs(c);
    return t ? { title: `${t}: ${c.d.shortNameA ?? c.d.participantA} ${c.d.spread ?? ''}`.trim(), subtitle: dot('Spread', c.d.measure, c.d.sport) } : null;
  },
  sportsTotal: (c) => {
    const t = vs(c);
    return t && c.d.line ? { title: `${t}: total ${formatNumber(cleanDecimal(c.d.line))}`, subtitle: dot('Total', c.d.measure, c.d.sport) } : null;
  },
  sportsOverUnderMarket: (c) => {
    const t = fill('{event}: {measure} over/under {line}', c);
    return t ? { title: t, subtitle: c.d.sport } : null;
  },
  sportsScalarMarket: (c) => {
    const t = fill('{measure} from {low} to {high}', c);
    return t ? { title: c.d.event ? `${c.d.event}: ${t}` : t, subtitle: dot('Scalar payout', c.d.sport) } : null;
  },
  aiModelHeadToHead: (c) => {
    const t = fill('{shortNameA} vs {shortNameB}', c);
    return t ? { title: join(`${t}${c.d.leaderboard ? `: ${c.d.leaderboard}` : ''}`, c.d.dateTime && `on ${v(c, 'dateTime')}`), subtitle: dot(c.d.modelA, c.d.modelB) } : null;
  },
  companyIpoConfirmed: (c) => {
    const t = fill('{company} IPO confirmed by {dateTime}?', c);
    return t ? { title: t } : null;
  },
  companyIpoFirstDayMarketCap: (c) => {
    const t = fill('{company} first-day market cap above ${marketCapThresholdB}B?', c);
    return t ? { title: t, subtitle: c.d.listingDeadline ? `If listed by ${v(c, 'listingDeadline')}` : undefined } : null;
  },
  awardEntryWins: (c) => {
    const t = fill('{displayName} wins {period} {award}?', c);
    return t ? { title: t, subtitle: c.d.field } : null;
  },
};
OUTCOME_RENDERERS.scalePrice = OUTCOME_RENDERERS.scalarPrice!;

/** Question families. */
const QUESTION_RENDERERS: Record<string, Renderer> = {
  sportsContestResult: (c) => {
    const t = vs(c);
    return t ? { title: t, subtitle: contest(c) } : null;
  },
  sportsTournamentWinner: (c) => {
    const t = fill('{season} {competition} winner', c) ?? fill('{competition} winner', c);
    return t ? { title: t, subtitle: c.d.sport } : null;
  },
  awardWinner: (c) => {
    const t = fill('{period} {award} winner', c) ?? fill('{award} winner', c);
    return t ? { title: t, subtitle: c.d.field } : null;
  },
  policyRateDecision: (c) => {
    const t = fill('{institution} {decisionLabel} rate decision', c) ?? fill('{institution} rate decision', c);
    return t ? { title: t, subtitle: c.d.policyMeasure } : null;
  },
};
QUESTION_RENDERERS.policyRateLadder = QUESTION_RENDERERS.policyRateDecision!;

/** Recurring series (name "Recurring", description class:…). */
function recurringOutcome(c: Ctx): Rendered | null {
  if (c.d.class === 'priceBinary') {
    const t = fill('{underlying} above {targetPrice} at {expiry}?', c);
    return t ? { title: t, subtitle: dot('Recurring', periodLabel(c.d.period)) } : null;
  }
  return null;
}

function recurringQuestion(c: Ctx): Rendered | null {
  if (c.d.class === 'priceBucket') {
    const t = fill('{underlying} price at {expiry}', c);
    return t ? { title: t, subtitle: dot('Recurring', periodLabel(c.d.period)) } : null;
  }
  return null;
}

/** Fixed labels of question members whose template has no keywords. */
const FIXED_LABELS: Record<string, string> = {
  sportsContestDraw: 'Draw',
  awardOther: 'Other',
  policyRateDecrease: 'Decrease',
  policyRateIncrease: 'Increase',
  policyRateNoChange: 'No change',
  sportsTournamentNoWinner: 'No winner',
};

const FALLBACK_NAMES = new Set(['template fallback', 'Recurring Fallback', 'Fallback']);

/** Bucket label for "Recurring Named Outcome" index k of a priceBucket question (0-based, ascending thresholds). */
function bucketLabel(q: QuestionSpec | undefined, index: string | undefined): string | null {
  if (!q || index === undefined || !/^\d+$/.test(index)) return null;
  const qd = parseDescription(q.description);
  const unit = priceUnit(qd.underlying);
  const t = (qd.priceThresholds ?? '').split(',').map((s) => s.trim()).filter(Boolean).map((s) => formatWithUnit(s, unit));
  const k = Number(index);
  if (!t.length || k > t.length) return null;
  if (k === 0) return `Below ${t[0]}`;
  if (k === t.length) return `Above ${t[t.length - 1]}`;
  return `${t[k - 1]} – ${t[k]}`;
}

/** An outcome's own label inside its question, or null if it is not a question member we understand. */
function memberLabel(o: OutcomeLike, ctx: Ctx, info: TemplateInfo | undefined, q: QuestionSpec | undefined): string | null {
  const name = typeof o.name === 'string' ? o.name : '';
  if (FALLBACK_NAMES.has(name)) return 'Other';
  if (name === 'Recurring Named Outcome') return bucketLabel(q, ctx.d.index);
  const fixed = FIXED_LABELS[ctx.family];
  if (fixed) return fixed;
  if (ctx.d.participant) return ctx.d.participant;
  if (ctx.d.candidate) return ctx.d.candidate;
  if (ctx.d.bucketLabel) return ctx.d.bucketLabel;
  if (info?.role === 'questionOutcome') return fill(info.name, ctx);
  return null;
}

// ---------------------------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------------------------

const metaIndexCache = new WeakMap<object, Map<number, QuestionSpec>>();

/** outcome id → its question (fallback, named and settled members). Memoized per meta object. */
export function questionIndex(meta: MetaLike | null | undefined): Map<number, QuestionSpec> {
  if (!meta) return new Map();
  let m = metaIndexCache.get(meta);
  if (!m) {
    m = new Map();
    for (const q of meta.questions ?? []) {
      for (const id of [q.fallbackOutcome, ...(q.namedOutcomes ?? []), ...(q.settledNamedOutcomes ?? [])]) {
        if (typeof id === 'number' && !m.has(id)) m.set(id, q);
      }
    }
    metaIndexCache.set(meta, m);
  }
  return m;
}

function ctxFor(name: string, description: string, opts: DescribeOptions) {
  const reg = opts.templates ?? snapshotRegistry();
  const id = templateId(name);
  const family = id ? familyOf(id) : name === 'Recurring' ? 'Recurring' : '';
  const info = id ? reg.get(id) : undefined;
  const ctx: Ctx = { family, d: parseDescription(description), opts };
  return { reg, id, family, info, ctx };
}

const qCache = new WeakMap<object, { key: string; reg: TemplateRegistry; desc: QuestionDescription }>();

function optsKey(opts: DescribeOptions): string {
  return `${getLocale()}|${opts.timeZone ?? ''}|${(opts.now ?? new Date()).getUTCFullYear()}`;
}

/** Title etc. for a categorical question. */
export function describeQuestion(q: QuestionSpec, opts: DescribeOptions = {}): QuestionDescription {
  const key = optsKey(opts);
  const reg = opts.templates ?? snapshotRegistry();
  const hit = qCache.get(q);
  if (hit && hit.key === key && hit.reg === reg) return hit.desc;
  const desc = describeQuestionUncached(q, { ...opts, templates: reg });
  qCache.set(q, { key, reg, desc });
  return desc;
}

function describeQuestionUncached(q: QuestionSpec, opts: DescribeOptions): QuestionDescription {
  const name = typeof q.name === 'string' ? q.name : '';
  const description = typeof q.description === 'string' ? q.description : '';
  const { reg, id, family, info, ctx } = ctxFor(name, description, opts);
  let r: Rendered | null = null;
  if (family === 'Recurring') r = recurringQuestion(ctx);
  else if (id) {
    r = QUESTION_RENDERERS[family]?.(ctx) ?? null;
    if (!r && info) {
      const t = fill(info.name, ctx);
      if (t) r = { title: t };
    }
  }
  const keyValue = isKeyValueDescription(description);
  if (r) {
    return {
      question: q.question,
      ...r,
      category: categoryOf(family, info, reg),
      ...resolution(ctx.d),
      details: [...detailsOf(ctx), ...(id ? [{ label: t('describe.detail.template'), value: id }] : [])],
      template: id ?? undefined,
      raw: false,
    };
  }
  // Free-text question ("May CPI year-over-year") or an unknown template: show what we have.
  const freeText = !id && name !== 'Recurring' && name !== '';
  const meta = splitMetadata(description);
  return {
    question: q.question,
    title: freeText ? name : name || `Question #${q.question}`,
    subtitle: keyValue ? undefined : meta.text || undefined,
    category: categoryOf(family, info, reg, meta.category),
    ...resolution(ctx.d),
    details: keyValue ? detailsOf(ctx) : [],
    template: id ?? undefined,
    raw: !freeText,
  };
}

const oCache = new WeakMap<object, { key: string; reg: TemplateRegistry; q: QuestionSpec | undefined; desc: OutcomeDescription }>();

/**
 * Human-readable description of one outcome. `meta` (the outcomeMeta response, or anything with `questions`)
 * lets question members be titled "<question>: <label>"; pass `{ questions: [q] }` or null as needed.
 */
export function describeOutcome(
  outcome: OutcomeLike,
  meta?: MetaLike | null,
  opts: DescribeOptions = {},
): OutcomeDescription {
  const q = questionIndex(meta).get(outcome.outcome);
  const key = optsKey(opts);
  const reg = opts.templates ?? snapshotRegistry();
  const hit = oCache.get(outcome);
  if (hit && hit.key === key && hit.reg === reg && hit.q === q) return hit.desc;
  const desc = describeOutcomeUncached(outcome, q, { ...opts, templates: reg });
  oCache.set(outcome, { key, reg, q, desc });
  return desc;
}

function sidesOf(o: OutcomeLike): [string, string] {
  const meta = {
    description: typeof o.description === 'string' ? o.description : '',
    sideSpecs: Array.isArray(o.sideSpecs) ? o.sideSpecs : [],
  };
  return [sideName(meta, 0), sideName(meta, 1)];
}

function describeOutcomeUncached(o: OutcomeLike, q: QuestionSpec | undefined, opts: DescribeOptions): OutcomeDescription {
  const name = typeof o.name === 'string' ? o.name : '';
  const description = typeof o.description === 'string' ? o.description : '';
  const { reg, id, family, info, ctx } = ctxFor(name, description, opts);
  const base = { outcome: o.outcome, sideLabels: sidesOf(o), template: id ?? undefined };
  const ownDetails = () => [...detailsOf(ctx), ...(id ? [{ label: t('describe.detail.template'), value: id }] : [])];

  // Question member?
  const label = memberLabel(o, ctx, info, q);
  if (q || label !== null || info?.role === 'questionOutcome') {
    const qd = q ? describeQuestion(q, opts) : undefined;
    const own = label ?? (name && !id ? name : q ? description || id : null);
    if (own !== null || qd) {
      const parentInfo = info?.parent ? reg.get(info.parent) : undefined;
      const title = qd && own ? `${qd.title}: ${own}` : (own ?? qd?.title ?? name);
      return {
        ...base,
        title,
        subtitle: qd?.subtitle ?? (!isKeyValueDescription(description) && description && description !== 'other' ? description : undefined),
        label: own ?? undefined,
        resolvesAt: qd?.resolvesAt,
        resolvesLabel: qd?.resolvesLabel,
        category: qd?.category ?? categoryOf(info?.parent ? familyOf(info.parent) : family, parentInfo ?? info, reg),
        details: [...(qd?.details ?? []), ...(isKeyValueDescription(description) ? detailsOf(ctx) : []), ...(id ? [{ label: t('describe.detail.outcome_template'), value: id }] : [])],
        question: q?.question,
        questionTitle: qd?.title,
        raw: false,
      };
    }
  }

  // Standalone outcome.
  let r: Rendered | null = null;
  if (family === 'Recurring') r = recurringOutcome(ctx);
  else if (id) {
    r = OUTCOME_RENDERERS[family]?.(ctx) ?? null;
    if (!r && info) {
      const t = fill(info.name, ctx);
      if (t) r = { title: t };
    }
  }
  if (r) {
    const touch = family === TOUCH_FAMILY ? touchInfo(name, description) : null;
    const details = ownDetails();
    if (touch) details.splice(details.length - (id ? 1 : 0), 0, { label: t('describe.detail.touch_rule'), value: touch.rule });
    return {
      ...base,
      ...r,
      category: categoryOf(family, info, reg),
      ...resolution(ctx.d, family),
      details,
      ...(touch ? { touch } : {}),
      raw: false,
    };
  }

  // Free text ("Below 4.3%") or something we do not understand: raw name + description, nothing hidden.
  const keyValue = isKeyValueDescription(description);
  const freeText = name !== '' && !id && name !== 'Recurring';
  const meta = splitMetadata(description);
  return {
    ...base,
    title: name || `Outcome #${o.outcome}`,
    subtitle: (keyValue ? description : meta.text) || undefined,
    category: id || meta.category ? categoryOf(family, info, reg, meta.category) : 'other',
    ...resolution(ctx.d, family),
    details: keyValue ? ownDetails() : [],
    raw: !freeText || keyValue,
  };
}

/**
 * Title only, for places that just need a label; "Outcome #<id>" when the outcome is unknown. The third argument
 * is either the outcome's question (used as-is, even if it does not list the outcome) or outcomeMeta.
 */
export function outcomeTitle(
  meta: OutcomeLike | undefined,
  outcome: number,
  questionOrMeta?: QuestionSpec | MetaLike | null,
  opts: DescribeOptions = {},
): string {
  if (!meta) return `Outcome #${outcome}`;
  if (questionOrMeta && 'question' in questionOrMeta) {
    return describeOutcomeUncached(meta, questionOrMeta, { ...opts, templates: opts.templates ?? snapshotRegistry() }).title;
  }
  return describeOutcome(meta, questionOrMeta as MetaLike | null | undefined, opts).title;
}

// ---------------------------------------------------------------------------------------------
// Question grouping
// ---------------------------------------------------------------------------------------------

export interface QuestionGroup {
  question: QuestionSpec;
  title: string;
  subtitle?: string;
  category: Category;
  resolvesAt?: Date;
  resolvesLabel?: string;
  /** Live named outcomes, in namedOutcomes order. */
  outcomes: OutcomeSpec[];
  /** The question's fallback ("Other") outcome, if still listed. */
  fallback?: OutcomeSpec;
  /** Named outcomes already settled (settledNamedOutcomes; hidden from the list). */
  settled: number[];
}

export interface Grouping {
  groups: QuestionGroup[];
  /** Outcomes that belong to no question, in input order. */
  ungrouped: OutcomeSpec[];
}

/**
 * Split outcomeMeta into question groups + standalone outcomes. Only outcomes present in `meta.outcomes` are
 * listed (pass a filtered `outcomes` list to group a search result). Groups with no listed member are dropped.
 */
export function groupByQuestion(
  meta: MetaLike | null | undefined,
  opts: DescribeOptions = {},
  outcomes: readonly OutcomeSpec[] = meta?.outcomes ?? [],
): Grouping {
  const byId = new Map(outcomes.map((o) => [o.outcome, o]));
  const qOf = questionIndex(meta);
  const groups: QuestionGroup[] = [];
  for (const q of meta?.questions ?? []) {
    const settled = new Set(q.settledNamedOutcomes ?? []);
    const named = (q.namedOutcomes ?? []).filter((id) => !settled.has(id)).map((id) => byId.get(id)).filter((o): o is OutcomeSpec => !!o);
    const fallback = byId.get(q.fallbackOutcome);
    if (named.length === 0 && !fallback) continue;
    const qd = describeQuestion(q, opts);
    groups.push({
      question: q,
      title: qd.title,
      subtitle: qd.subtitle,
      category: qd.category,
      resolvesAt: qd.resolvesAt,
      resolvesLabel: qd.resolvesLabel,
      outcomes: named,
      fallback,
      settled: [...settled],
    });
  }
  const ungrouped = outcomes.filter((o) => !qOf.has(o.outcome));
  return { groups, ungrouped };
}
